import { useState, useEffect } from 'react';
import { Stack, Box, Typography, CircularProgress, Button, Alert } from '@mui/material';
import { usePhrase } from '@/hooks/general/usePhrase/usePhrase';
import { IDENTITY_PHRASES } from '@/consts/phrases/registrationPhrases';
import { FetchErrorsType } from '@/types/types/general/errors';
import { registerService } from '@/services/api/registerService/registerService';
import { useRegistrationStore } from '@/store/pages/registrationStore/registrationStore';
import { getFetchErrors } from '@/utils/getFetchErrorMessage';
import { ErrorAlert } from '@/components/forms/alerts/ErrorAlert/ErrorAlert';
import { isMobile } from 'react-device-detect';
import { useRegistrationDetailsQuery } from '@/hooks/queries/registration/generalRegistrationQueries';
import { IdentityRegistrationYotiFrame } from '@/components/blocks/identity/IdentityRegistrationYotiFrame/IdentityRegistrationYotiFrame';
import { FadeIn } from '@/components/effects/FadeIn';
import { YotiStepsDescriptionBox } from '@/components/blocks/identity/YotiStepsDescriptionBox/YotiStepsDescriptionBox';
import { useConfigStore } from '@/store/configStore/configStore';
import { useSendRelevantIdData } from '@/hooks/forms/register/useSendRelevantIdData';
import { config } from '@/config';
import { RVIDForm } from '../RVIDForm/RVIDForm';
import * as Sentry from '@sentry/nextjs';

import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';

import styles from './RegistrationIdentityForm.module.css';

interface RegistrationIdentityFormProps {
    relevantIdRequestId?: string;
    isRegistrationDataLoading: boolean;
    revalidateRegistrationData: () => Promise<void>;
    state: {
        mobileIdentity: boolean;
        setMobileIdentity: (val: boolean) => void;
        yotiVisible: boolean;
        setYotiVisible: (val: boolean) => void;
        isYotiCompleted: boolean;
    };
}

export const RegistrationIdentityForm = ({
    relevantIdRequestId,
    isRegistrationDataLoading,
    revalidateRegistrationData,
    state: { mobileIdentity, setMobileIdentity, yotiVisible, setYotiVisible, isYotiCompleted },
}: RegistrationIdentityFormProps) => {
    const { t } = usePhrase();

    const { goToNextStep, registrationId, setYotiDetails, yotiSessionID, yotiSessionToken, reset } =
        useRegistrationStore((state) => ({
            goToNextStep: state.goToNextStep,
            registrationId: state.registrationId,
            setYotiDetails: state.setYotiDetails,
            yotiSessionID: state.yotiSessionID,
            yotiSessionToken: state.yotiSessionToken,
            reset: state.reset,
        }));

    const [errorMessage, setErrorMessage] = useState<FetchErrorsType>(undefined);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [chooseStep, setChooseStep] = useState(false);
    const [dataListening, setIsDataListening] = useState(false);

    const { data: registrationData } = useRegistrationDetailsQuery(
        dataListening,
        config.timeout.yotiIdentityRevalidate,
    );

    const relevantIdSurveyId = useConfigStore((state) => state.registration_relevantid_survey_id);

    const hasRelevantId = Boolean(relevantIdRequestId);
    const [wasRelevantIdScriptLoaded, setWasRelevantIdScriptLoaded] = useState(false);

    const { isRelevantIdDataLoading } = useSendRelevantIdData(
        registrationId,
        relevantIdRequestId,
        relevantIdSurveyId,
        wasRelevantIdScriptLoaded,
        isRegistrationDataLoading,
    );

    const handleYoti = async ({
        manualVerification,
        completeOnMobile,
    }: {
        manualVerification: boolean;
        completeOnMobile: boolean;
    }) => {
        if (!registrationId) {
            Sentry.captureException(
                new Error('Attempted to submit form but no registration id existed'),
            );
            reset();
            return;
        }
        const registrationData = await registerService.putIdentity(registrationId, {
            manualVerification,
            completeOnMobile,
        });
        const { yotiSessionID, yotiSessionToken } = registrationData;
        if (completeOnMobile || manualVerification) return;
        if (!yotiSessionID || !yotiSessionToken) {
            const error = new Error('Recieved Yoti data is incorrect');
            Sentry.captureException(error, { extra: { registrationData } });
            throw error;
        }
        setYotiDetails({ yotiSessionID, yotiSessionToken });
    };

    const handleSkipStep = async () => {
        try {
            setIsSubmitting(true);
            await handleYoti({
                manualVerification: true,
                completeOnMobile: false,
            });
            setErrorMessage(undefined);
            goToNextStep();
            setIsSubmitting(false);
            await revalidateRegistrationData();
        } catch (err) {
            setIsSubmitting(false);
            setErrorMessage(getFetchErrors(err));
        }
    };

    const handleStartYoti = async () => {
        try {
            setIsSubmitting(true);
            await handleYoti({
                manualVerification: false,
                completeOnMobile: false,
            });
            setErrorMessage(undefined);
            setYotiVisible(true);
            setIsSubmitting(false);
        } catch (err) {
            setYotiVisible(false);
            setIsSubmitting(false);
            setErrorMessage(getFetchErrors(err));
        }
    };

    const handleStartOnMobile = async () => {
        try {
            setIsSubmitting(true);
            await handleYoti({
                manualVerification: false,
                completeOnMobile: true,
            });
            setErrorMessage(undefined);
            setMobileIdentity(true);
            setIsSubmitting(false);
        } catch (err) {
            setMobileIdentity(false);
            setIsSubmitting(false);
            setErrorMessage(getFetchErrors(err));
        }
    };

    const handleStartIdentity = async () => {
        if (isMobile) {
            await handleStartYoti();
            setChooseStep(false);
        } else {
            setYotiVisible(false);
            setChooseStep(true);
        }
    };

    const resetIdentityStepState = () => {
        setMobileIdentity(false);
        setYotiVisible(false);
        setChooseStep(false);
    };

    useEffect(() => {
        if (mobileIdentity) {
            setIsDataListening(true);
        }
        if (isYotiCompleted) {
            goToNextStep();
            setMobileIdentity(false);
            setYotiVisible(false);
        }
        return () => setIsDataListening(false);
    }, [
        revalidateRegistrationData,
        mobileIdentity,
        goToNextStep,
        registrationData,
        setMobileIdentity,
        setYotiVisible,
        isYotiCompleted,
    ]);

    const ReturnBackButton = () => (
        <Stack
            sx={{
                alignItems: 'center',
                justifyContent: 'center',
                my: 2,
            }}
        >
            <Button
                variant="text"
                color="inherit"
                onClick={resetIdentityStepState}
                size="small"
                startIcon={<KeyboardBackspaceIcon />}
                sx={{ fontSize: '12px' }}
            >
                {t(IDENTITY_PHRASES.CANCEL_AWAIT_BUTTON)}
            </Button>
        </Stack>
    );

    return (
        <>
            {errorMessage && (
                <Box sx={{ mb: 2 }}>
                    <ErrorAlert id="message_box" errorMessage={errorMessage} />
                </Box>
            )}
            {mobileIdentity ? (
                <>
                    <Stack
                        sx={{
                            alignItems: 'center',
                            justifyContent: 'center',
                            px: 2,
                        }}
                    >
                        <Alert severity="info">
                            {t(IDENTITY_PHRASES.AWAIT_COMPLETION_LOADING_TEXT)}
                        </Alert>
                    </Stack>
                    <Stack
                        sx={{
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '190px',
                        }}
                    >
                        <CircularProgress data-testid="yotiMobilePreloader" />
                    </Stack>
                    <ReturnBackButton />
                </>
            ) : (
                <>
                    {yotiVisible && registrationId ? (
                        <FadeIn>
                            <IdentityRegistrationYotiFrame
                                registrationId={registrationId}
                                yotiId={yotiSessionID}
                                yotiToken={yotiSessionToken}
                                setErrorMessage={setErrorMessage}
                                revalidateRegistrationData={revalidateRegistrationData}
                            />
                            <ReturnBackButton />
                        </FadeIn>
                    ) : (
                        <>
                            <YotiStepsDescriptionBox />
                            <Box>
                                {chooseStep ? (
                                    <FadeIn duration={600}>
                                        <Stack
                                            className={styles.navigation}
                                            spacing={3}
                                            sx={{
                                                alignItems: 'center',
                                                mt: 3,
                                                mx: 'auto',
                                            }}
                                        >
                                            <Button
                                                onClick={handleStartOnMobile}
                                                variant="contained"
                                                color="primary"
                                                className={styles.navButton}
                                                disabled={isSubmitting}
                                                loading={isSubmitting}
                                            >
                                                {t(IDENTITY_PHRASES.MOBILE_BUTTON)}
                                            </Button>
                                            <Button
                                                onClick={handleStartYoti}
                                                variant="outlined"
                                                color="primary"
                                                className={styles.navButton}
                                                disabled={isSubmitting}
                                                loading={isSubmitting}
                                            >
                                                {t(IDENTITY_PHRASES.DESKTOP_BUTTON)}
                                            </Button>
                                        </Stack>
                                    </FadeIn>
                                ) : (
                                    <Stack
                                        className={styles.navigation}
                                        spacing={3}
                                        sx={{
                                            alignItems: 'center',
                                            mt: 3,
                                            mx: 'auto',
                                        }}
                                    >
                                        <Button
                                            onClick={handleStartIdentity}
                                            variant="contained"
                                            color="primary"
                                            className={styles.navButton}
                                            disabled={isSubmitting}
                                            loading={isSubmitting}
                                        >
                                            {t(IDENTITY_PHRASES.AGREE_TO_CONFIRM_BUTTON)}
                                        </Button>
                                        <Button
                                            onClick={handleSkipStep}
                                            variant="outlined"
                                            color="primary"
                                            className={styles.navButton}
                                            disabled={isSubmitting || isRelevantIdDataLoading}
                                            loading={isSubmitting || isRelevantIdDataLoading}
                                        >
                                            {t(IDENTITY_PHRASES.SKIP_CONFIRMATION_BUTTON)}
                                        </Button>
                                    </Stack>
                                )}
                            </Box>
                        </>
                    )}
                </>
            )}
            <Box className={styles.infoMessage}>
                <Typography variant="subtitle2">{t(IDENTITY_PHRASES.YOTI_NOTES)}</Typography>
            </Box>
            {hasRelevantId && (
                <RVIDForm setWasRelevantIdScriptLoaded={setWasRelevantIdScriptLoaded} />
            )}
        </>
    );
};
